import { convertJsonToFieldValueAvroWithoutTypename, isContainer, isModule } from 'layout-data-lib/GridLayoutDataTree/typenameUtils';
import Raven from 'raven-js';
const JacksonTypes = {
  STRING: 'string',
  INTEGER: 'integer',
  LONG: 'long',
  DOUBLE: 'double',
  BOOLEAN: 'boolean',
  LIST: 'list',
  MAP: 'map',
  NULL: 'null'
};
const PRIMITIVE_TYPES = [JacksonTypes.STRING, JacksonTypes.INTEGER, JacksonTypes.LONG, JacksonTypes.DOUBLE, JacksonTypes.BOOLEAN, JacksonTypes.NULL];

// Map __typename to @type
const RFC_TO_JACKSON_TYPE_MAP = {
  'com.hubspot.chirp.ext.models.StringFieldValue': JacksonTypes.STRING,
  'com.hubspot.chirp.ext.models.IntFieldValue': JacksonTypes.INTEGER,
  'com.hubspot.chirp.ext.models.LongFieldValue': JacksonTypes.LONG,
  'com.hubspot.chirp.ext.models.DoubleFieldValue': JacksonTypes.DOUBLE,
  'com.hubspot.chirp.ext.models.BooleanFieldValue': JacksonTypes.BOOLEAN,
  'com.hubspot.chirp.ext.models.ListFieldValue': JacksonTypes.LIST,
  'com.hubspot.chirp.ext.models.MapFieldValue': JacksonTypes.MAP,
  'com.hubspot.chirp.ext.models.NullFieldValue': JacksonTypes.NULL
};
const getJacksonTypeFromTypename = typename => {
  const mappedType = RFC_TO_JACKSON_TYPE_MAP[typename];
  if (mappedType) {
    return mappedType;
  }
  Raven.captureMessage(`Grids layout normalization: unmapped typename: ${typename}`, {
    extra: {
      typename
    }
  });
  return undefined;
};
const isPrimitiveType = type => PRIMITIVE_TYPES.includes(type);

// AI Editor layout gen RPC returns FieldValue with __typename, but without @type
// We need to to add @type to module.fieldOverrides to make these generated sections compatible with Editor 1.0 buffer
// Adapted from convertJsonToFieldValueAvroWithoutTypename
const addJacksonTypeToFieldValue = value => {
  // If value is null or undefined, return as-is (will be handled by convertJsonToFieldValueAvroWithoutTypename if needed)
  if (value === null || value === undefined) {
    return value;
  }

  // Check if this is already a FieldValue object with __typename
  if (typeof value === 'object' && !Array.isArray(value) && '__typename' in value) {
    const fieldValue = value;
    const typename = fieldValue.__typename;
    const mappedType = getJacksonTypeFromTypename(typename);
    if (!mappedType) {
      // Unknown typename, return as-is
      return value;
    }

    // If @type already exists, check if we need to recursively process the value
    if ('@type' in fieldValue) {
      if ('value' in fieldValue) {
        const existingType = fieldValue['@type'];
        // Only recursively process complex types (list, map), not primitives
        // Primitive FieldValue types should not have their value recursively processed
        if (!isPrimitiveType(existingType)) {
          return Object.assign({}, fieldValue, {
            value: addJacksonTypeToFieldValue(fieldValue.value)
          });
        }
      }
      return value;
    }

    // Add @type and conditionally process the value property
    const result = Object.assign({}, fieldValue, {
      '@type': mappedType
    });
    if ('value' in fieldValue) {
      // Only recursively process complex types (list, map), not primitives
      if (!isPrimitiveType(mappedType)) {
        result.value = addJacksonTypeToFieldValue(fieldValue.value);
      } else {
        // For primitive types, keep the value as-is
        result.value = fieldValue.value;
      }
    }
    return result;
  }

  // For arrays, recursively process each item
  if (Array.isArray(value)) {
    return value.map(item => addJacksonTypeToFieldValue(item));
  }

  // For plain objects without __typename, recursively process their properties
  if (typeof value === 'object') {
    const obj = value;
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = addJacksonTypeToFieldValue(obj[key]);
      }
    }
    return result;
  }
  return convertJsonToFieldValueAvroWithoutTypename(value);
};
export const normalizeModuleFieldOverrides = gridContent => {
  if (isModule(gridContent)) {
    const module = gridContent;
    if (!module.fieldOverrides) {
      return module;
    }
    const normalizedFieldOverrides = {};
    for (const [key, value] of Object.entries(module.fieldOverrides)) {
      normalizedFieldOverrides[key] = addJacksonTypeToFieldValue(value);
    }
    return Object.assign({}, module, {
      fieldOverrides: normalizedFieldOverrides
    });
  } else if (isContainer(gridContent)) {
    const container = gridContent;
    if (!container.children) {
      return container;
    }
    const normalizedChildren = container.children.map(child => normalizeModuleFieldOverrides(child));
    return Object.assign({}, container, {
      children: normalizedChildren
    });
  }
  return gridContent;
};